import { Align, Justify, Row, Theme } from '@busybusy/webapp-react-ui';
import { PenIcon } from 'assets/icons';
import classNames from 'classnames';
import { Spinner } from 'components';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentRibbon from 'components/foundation/dashboard/DashboardContentRibbon/DashboardContentRibbon';
import DashboardEnableCard from 'components/foundation/dashboard/DashboardEnableCard/DashboardEnableCard';
import DashboardSingleRibbon from 'components/foundation/dashboard/DashboardSingleRibbon/DashboardSingleRibbon';
import {
  DailySignOffIssueStatus,
  DailySignOffIssueType,
} from 'containers/daily-sign-off/DailySignOffFilter/DailySignOffReportFilter';
import useDailySignatureAlertsStatus from 'containers/dashboard/hooks/useDailySignatureAlertsStatus';
import { useDashboardTimeRange } from 'containers/dashboard/hooks/useDashboardTimeRange';
import { getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { useActiveMember, useDailySignatureSettingNavigation, useDailySignOffNavigation, useOrganization } from 'hooks';
import useLegacyNavigation from 'hooks/navigation/useLegacyNavigation';
import { t } from 'i18next';
import { sortBy } from 'lodash';
import * as React from 'react';
import TimeRangeType from 'types/TimeRangeType';
import { useFeatureFlags } from 'utils/features';
import { isAdmin } from 'utils/positionUtils';
import DashboardErrorCard from '../DashboardErrorCard/DashboardErrorCard';
import './DailySignatureAlertsCard.scss';

export interface IDailySignatureAlertsCardProps {
  className?: string | object;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide?: () => void;
}

enum OptionsKey {
  INJURY = 'injury',
  INACCURATE_TIME = 'inaccurate-time',
  BREAK_ISSUES = 'break-issues',
}

const DailySignatureAlertsCard = (props: IDailySignatureAlertsCardProps) => {
  const { className, theme, setting, onHide } = props;

  const isPro = useFeatureFlags('PRO');
  const organization = useOrganization();
  const timeRange = useDashboardTimeRange(setting.options?.time ?? 'pastSeven');
  const signatureStatus = useDailySignatureAlertsStatus(timeRange, {
    hideProcessedInjuries: setting.options!.hideProcessedInjuries,
    hideResolvedInaccuracies: setting.options!.hideResolvedInaccurateTime,
    hideResolvedBreaks: setting.options!.hideResolvedBreakIssues,
  });
  const legacyNavigation = useLegacyNavigation();
  const dailySignatureSettingNavigation = useDailySignatureSettingNavigation();
  const dailySignOffNavigation = useDailySignOffNavigation();
  const activeMember = useActiveMember();
  const canManage = (activeMember.position?.manageTimeEntries ?? 0) > 0;

  function isInjuryEnabled() {
    const injuryKey = 'injury';
    if (setting.options && !setting.options![injuryKey]) {
      return false;
    }
    return organization.safetySignature ?? false;
  }

  function isTimeAccuracyEnabled() {
    const inaccurateTimeKey = 'inaccurateTime';
    if (setting.options && !setting.options![inaccurateTimeKey]) {
      return false;
    }
    return organization.timeAccuracy ?? false;
  }

  function isBreakPolicyEnabled() {
    const breakHoursKey = 'breakHours';
    if (setting.options && !setting.options![breakHoursKey]) {
      return false;
    }
    return organization.breakPolicy ?? false;
  }

  const enabledOptions = [
    {
      enabled: isInjuryEnabled(),
      value: signatureStatus.aggregatedInfo?.injuryCount ?? 0,
      title: t('Injuries'),
      key: OptionsKey.INJURY,
      hideResolved: setting.options!.hideProcessedInjuries,
      position: setting.options!.injuriesPosition,
    },
    {
      enabled: isTimeAccuracyEnabled(),
      value: signatureStatus.aggregatedInfo?.inaccurateTimeCount ?? 0,
      title: t('Inaccurate Time'),
      key: OptionsKey.INACCURATE_TIME,
      hideResolved: setting.options!.hideResolvedInaccurateTime,
      position: setting.options!.inaccurateTimePosition,
    },
    {
      enabled: isBreakPolicyEnabled(),
      value: signatureStatus.aggregatedInfo?.breakPolicyCount ?? 0,
      title: t('Break Issues'),
      key: OptionsKey.BREAK_ISSUES,
      hideResolved: setting.options!.hideResolvedBreakIssues,
      position: setting.options!.breakIssuesPosition,
    },
  ].filter((item) => item.enabled);

  const subtitle = getDashboardTimeFrameTitle('', setting.options?.time ?? 'payPeriod') ?? '';

  const classes = classNames('daily-signature-alerts-card', className, { 'is-loading': signatureStatus.isLoading });

  function onCardSelection(key: OptionsKey) {
    const lookup: Record<
      OptionsKey,
      { filter_type: DailySignOffIssueType; filter_status: DailySignOffIssueStatus.OPEN | undefined }
    > = {
      [OptionsKey.INJURY]: {
        filter_type: DailySignOffIssueType.INJURED,
        filter_status: setting.options!.hideProcessedInjuries ? DailySignOffIssueStatus.OPEN : undefined,
      },
      [OptionsKey.INACCURATE_TIME]: {
        filter_type: DailySignOffIssueType.TIME_CORRECT,
        filter_status: setting.options!.hideResolvedInaccurateTime ? DailySignOffIssueStatus.OPEN : undefined,
      },
      [OptionsKey.BREAK_ISSUES]: {
        filter_type: DailySignOffIssueType.BREAK_COMPLIANCE,
        filter_status: setting.options!.hideResolvedBreakIssues ? DailySignOffIssueStatus.OPEN : undefined,
      },
    };

    dailySignOffNavigation({
      ...lookup[key],
      time_range_type: setting.options?.time === 'payPeriod' ? TimeRangeType.PAY_PERIOD : TimeRangeType.DAILY,
      start_date: setting.options?.time === 'payPeriod' ? timeRange.startTime.toFormat('M-d-yyyy') : undefined,
      end_date: setting.options?.time === 'payPeriod' ? timeRange.endTime.toFormat('M-d-yyyy') : undefined,
    });
  }

  return (
    <>
      {isPro ? (
        enabledOptions.length > 0 ? (
          <div className={classes}>
            {signatureStatus.error !== null ? (
              <DashboardErrorCard theme={theme} onClick={() => signatureStatus.setRefetch(!signatureStatus.refetch)} />
            ) : (
              sortBy(enabledOptions, 'position').map((option, index) => {
                const filteredText = option.key === OptionsKey.INJURY ? t('Unprocessed - ') : t('Unresolved - ');
                const onClick =
                  option.key === OptionsKey.BREAK_ISSUES
                    ? canManage
                      ? () => onCardSelection(option.key)
                      : undefined
                    : () => onCardSelection(option.key);
                if (enabledOptions.length === 1) {
                  return (
                    <DashboardSingleRibbon key={option.title} theme={Theme.DARK} onClick={onClick}>
                      {signatureStatus.isLoading ? (
                        <Row justify={Justify.CENTER} align={Align.CENTER}>
                          <Spinner size="md" />
                        </Row>
                      ) : (
                        <>
                          <h1 className={classNames('mb-5', 'single-ribbon-value')}>{option.value}</h1>
                          <div className={'single-ribbon-title'}>{option.title}</div>
                          <div className="single-ribbon-subtitle">
                            {option.hideResolved ? filteredText + subtitle : subtitle}
                          </div>
                        </>
                      )}
                    </DashboardSingleRibbon>
                  );
                } else {
                  return (
                    <DashboardContentRibbon
                      className={classNames({ 'mt-6': index !== 0 })}
                      key={option.title}
                      onClick={onClick}
                      title={option.title}
                      subtitle={option.hideResolved ? filteredText + subtitle : subtitle}
                      value={option.value.toString()}
                      theme={theme}
                      isLoading={signatureStatus.isLoading}
                    />
                  );
                }
              })
            )}
          </div>
        ) : activeMember.position?.manageCompanySettings ? (
          <DashboardEnableCard
            className={`${signatureStatus.isLoading ? 'is-loading' : ''}`}
            title={setting.title}
            svg={PenIcon}
            buttonLabel={t('Enable Daily Sign-Off')}
            onClick={dailySignatureSettingNavigation}
            theme={theme}
            onHide={onHide}
          />
        ) : (
          <EmptyLoadingCard isLoading={signatureStatus.isLoading} />
        )
      ) : isAdmin(activeMember.position) ? (
        <DashboardEnableCard
          className={`${signatureStatus.isLoading ? 'is-loading' : ''}`}
          title={setting.title}
          svg={PenIcon}
          buttonLabel={t('Upgrade to Pro')}
          onClick={legacyNavigation.subscription}
          theme={theme}
          onHide={onHide}
        />
      ) : (
        <EmptyLoadingCard isLoading={signatureStatus.isLoading} />
      )}
    </>
  );
};

const EmptyLoadingCard: React.FC<{ isLoading: boolean }> = ({ isLoading }) =>
  isLoading ? <div className="is-loading" /> : null;

export default DailySignatureAlertsCard;
